import type { CatalogueBambu, Rapport, TeinteBambu } from "./contract";
import type { Colonne } from "./silhouettes-verdict";

/**
 * Les filaments du jeu (#337) : chaque couleur trouvée dans les deux rendus ramenée à un vrai PLA
 * Bambu, les corrections de Nicolas retenues dans le navigateur, la nomenclature (BOM) à copier
 * dans MakerWorld. Un module pur, sans React ni requête : seule la mémoire touche le navigateur
 * (`localStorage`), et elle ne lève jamais.
 *
 * L'ordre des filaments est celui de `products` (`_teintes_du_jeu`, `assemblage.py`) : la teinte
 * de la base d'abord, puis celles de la figurine de gauche dans l'ordre de ses couleurs, puis
 * celles de la figurine de droite, sans doublon. La page montre donc la liste que l'assemblage
 * rendra.
 */

/**
 * Le nom de la pièce d'une figurine d'une seule couleur, et son gris : ceux de l'outil (`base`
 * dans `figure.py`, `BASE_SANS_COULEUR` dans `face.py`). Le rapport n'a alors aucune couleur.
 */
export const COULEUR_UNIQUE = "base";
export const HEXA_UNIQUE = "#1b1b1b";

/** Un AMS a 4 emplacements, l'AMS lite de l'A1 mini aussi : au-delà, une figurine ne tient pas dedans. */
export const FILAMENTS_AMS_MAX = 4;

/** La clé de la mémoire dans `localStorage` : une teinte retenue par nom de couleur trouvée. */
export const CLE_MEMOIRE = "silhouettes.teintes";

const COLONNES: readonly Colonne[] = ["gauche", "droite"];

export type LigneFilament = {
  colonne: Colonne;
  /** Le nom de la couleur trouvée, celui du rapport ; `base` pour une figurine d'une seule couleur. */
  couleur: string;
  hexa: string;
  /** La teinte que l'outil propose pour cette couleur. */
  proposee: TeinteBambu;
  /** La teinte retenue : la correction, sinon la mémoire, sinon la proposition. */
  choisie: TeinteBambu;
  /** D'où vient `choisie` : la correction de cette page, la mémoire du navigateur, ou la proposition. */
  origine: "proposee" | "memoire" | "choix";
};

/**
 * La clé d'une correction : la colonne, puis la couleur. Deux figurines peuvent avoir chacune un
 * « blanc », et la correction de l'une ne vaut pas pour l'autre.
 */
export function cleDeChoix(colonne: Colonne, couleur: string): string {
  return `${colonne}:${couleur}`;
}

/**
 * La première habituelle que le catalogue connaît (Charcoal en général), sinon sa première
 * teinte. Le contrat refuse un catalogue sans teinte : en trouver un vide est une erreur de
 * programme, pas un cas de la page.
 */
function premiereHabituelle(catalogue: CatalogueBambu): TeinteBambu {
  for (const cle of catalogue.habituelles) {
    const teinte = catalogue.teintes.find((candidate) => candidate.cle === cle);
    if (teinte !== undefined) return teinte;
  }
  const premiere = catalogue.teintes[0];
  if (premiere === undefined) throw new Error("catalogue des teintes Bambu vide");
  return premiere;
}

/** Ce que `lignes` lit d'une couleur du rapport. */
type Couleur = { nom: string; hexa: string; bambu?: TeinteBambu | undefined };

/** Une figurine d'une seule couleur : le rapport n'en liste aucune, il y a une pièce `base`. */
const COULEUR_SEULE: readonly Couleur[] = [{ nom: COULEUR_UNIQUE, hexa: HEXA_UNIQUE }];

/**
 * La teinte du catalogue que `retenues` associe à `nom`, ou `undefined` : rien pour ce nom, une
 * valeur qui n'est pas une clé, ou une clé que le catalogue ne connaît plus.
 */
function teinteRetenue(
  retenues: Record<string, string>,
  nom: string,
  catalogue: ReadonlyMap<string, TeinteBambu>,
): TeinteBambu | undefined {
  const cle = retenues[nom];
  return typeof cle === "string" ? catalogue.get(cle) : undefined;
}

/**
 * Une ligne par couleur trouvée : la figurine de gauche d'abord, puis celle de droite, chacune
 * dans l'ordre de son rapport (la base d'abord). La teinte choisie est, dans cet ordre, la
 * correction faite dans la page (`choix`, clé `cleDeChoix`), la teinte que la mémoire du
 * navigateur garde pour ce nom de couleur, puis la proposition de l'outil. Une clé que le
 * catalogue ne connaît pas est ignorée, et la couche suivante prend le relais.
 *
 * Une couleur sans proposition (un rapport d'un `products` plus ancien que les propositions) et la
 * pièce `base` d'une figurine d'une seule couleur reçoivent la première habituelle. Une figurine
 * sans rendu n'a aucune ligne.
 */
export function lignes(
  rapports: { gauche: Rapport | null; droite: Rapport | null },
  catalogue: CatalogueBambu,
  memoire: Record<string, string>,
  choix: Record<string, string>,
): LigneFilament[] {
  const parCle = new Map(catalogue.teintes.map((teinte) => [teinte.cle, teinte] as const));
  return COLONNES.flatMap((colonne): LigneFilament[] => {
    const rapport = rapports[colonne];
    if (!rapport) return [];
    const listees = rapport.couleurs ?? [];
    const couleurs: readonly Couleur[] = listees.length > 0 ? listees : COULEUR_SEULE;
    return couleurs.map(({ nom, hexa, bambu }): LigneFilament => {
      const proposee = bambu ?? premiereHabituelle(catalogue);
      const ligne = { colonne, couleur: nom, hexa, proposee };
      const parChoix = teinteRetenue(choix, cleDeChoix(colonne, nom), parCle);
      if (parChoix !== undefined) return { ...ligne, choisie: parChoix, origine: "choix" };
      const parMemoire = teinteRetenue(memoire, nom, parCle);
      if (parMemoire !== undefined) return { ...ligne, choisie: parMemoire, origine: "memoire" };
      return { ...ligne, choisie: proposee, origine: "proposee" };
    });
  });
}

/**
 * Les filaments du jeu : la teinte de la base (Bookends et Socles) d'abord, puis les teintes
 * choisies dans l'ordre des lignes, sans doublon. Deux teintes sont la même quand elles ont la
 * même clé : Black et Charcoal ont le même hexa et sont deux filaments.
 */
export function filamentsDuJeu(
  toutes: readonly LigneFilament[],
  teinteBase: TeinteBambu,
): TeinteBambu[] {
  const filaments = new Map<string, TeinteBambu>();
  for (const teinte of [teinteBase, ...toutes.map((ligne) => ligne.choisie)]) {
    if (!filaments.has(teinte.cle)) filaments.set(teinte.cle, teinte);
  }
  return [...filaments.values()];
}

/**
 * La teinte des Bookends et des Socles tant que Nicolas n'y touche pas : celle de la base de la
 * figurine de gauche, sa première couleur, corrections comprises (spec, section 3 : Charcoal en
 * général). Sans rendu à gauche, la première habituelle.
 */
export function teinteBaseParDefaut(
  toutes: readonly LigneFilament[],
  catalogue: CatalogueBambu,
): TeinteBambu {
  const base = toutes.find((ligne) => ligne.colonne === "gauche");
  return base === undefined ? premiereHabituelle(catalogue) : base.choisie;
}

/** Le nombre de filaments de la figurine : des couleurs ramenées à la même teinte n'en font qu'un. */
export function filamentsDeLaFigure(toutes: readonly LigneFilament[], colonne: Colonne): number {
  const siennes = toutes.filter((ligne) => ligne.colonne === colonne);
  return new Set(siennes.map((ligne) => ligne.choisie.cle)).size;
}

/** Les figurines qui ont plus de 4 filaments : un AMS n'en tient pas plus, le jeu n'imprime pas d'un trait. */
export function figuresAuDelaDeLAms(toutes: readonly LigneFilament[]): Colonne[] {
  return COLONNES.filter((colonne) => filamentsDeLaFigure(toutes, colonne) > FILAMENTS_AMS_MAX);
}

/**
 * Ce que l'assemblage demande pour une figurine (`teintes` de la requête) : la clé de la teinte
 * choisie, par nom de pièce.
 *
 * L'assemblage relit le 3MF de la figurine, et une pièce unique y porte le nom `base`, quel que
 * soit le nom que le rapport donnait à sa couleur (`figure.py`, `lire_figure`). Une figurine à une
 * seule ligne répond donc `{ base: <clé> }`, qu'elle vienne d'un rapport sans couleurs ou d'un
 * rapport qui n'en liste qu'une.
 */
export function teintesDeLaRequete(
  toutes: readonly LigneFilament[],
  colonne: Colonne,
): Record<string, string> {
  const siennes = toutes.filter((ligne) => ligne.colonne === colonne);
  const [seule, ...autres] = siennes;
  if (seule !== undefined && autres.length === 0) return { [COULEUR_UNIQUE]: seule.choisie.cle };
  return Object.fromEntries(siennes.map((ligne) => [ligne.couleur, ligne.choisie.cle]));
}

/** La nomenclature : `<gamme> <nom anglais>` par filament, comme celle de l'assemblage. */
export function bom(filaments: readonly TeinteBambu[]): string {
  return filaments.map((teinte) => `${teinte.gamme} ${teinte.nom_en}`).join(", ");
}

/**
 * Ce que le navigateur retient des corrections de Nicolas : le nom d'une couleur trouvée vers la
 * clé de la teinte. Tout est lu sans confiance, et chaque accès à `localStorage` est sous `try` :
 * un navigateur qui refuse tout stockage, une valeur qui n'est pas du JSON, qui n'est pas un objet
 * ou dont une entrée n'est pas un texte donnent une mémoire vide, ou sans cette entrée, jamais une
 * exception. Une clé que le catalogue ne connaît plus est gardée ici et ignorée par `lignes`.
 */
export function lireMemoire(): Record<string, string> {
  try {
    const brut = window.localStorage.getItem(CLE_MEMOIRE);
    if (brut === null) return {};
    const lu: unknown = JSON.parse(brut);
    if (typeof lu !== "object" || lu === null || Array.isArray(lu)) return {};
    const memoire: Record<string, string> = {};
    for (const [couleur, cle] of Object.entries(lu)) {
      if (typeof cle === "string") memoire[couleur] = cle;
    }
    return memoire;
  } catch {
    return {};
  }
}

/**
 * Retient la teinte choisie pour une couleur, pour les rendus suivants. Ne lève jamais : un
 * navigateur qui ne garde rien (stockage bloqué, quota plein) perd la correction pour la page
 * suivante, pas pour celle-ci, qui la tient dans son état.
 */
export function retenir(couleur: string, cle: string): void {
  try {
    const memoire = { ...lireMemoire(), [couleur]: cle };
    window.localStorage.setItem(CLE_MEMOIRE, JSON.stringify(memoire));
  } catch {
    // Rien à faire : la correction vaut pour cette page seulement.
  }
}
